// Helper functions for guest cart management
import $ from 'jquery';

/**
 * Calculate total items across all guest cart types
 * @returns {number} Total count of items in guest cart
 */
export function getGuestCartTotalCount() {
  try {
    const guestProducts = JSON.parse(localStorage.getItem('guest_cart_products') || '[]');
    const guestMagazines = JSON.parse(localStorage.getItem('guest_cart_magazines') || '[]');
    const guestEvents = JSON.parse(localStorage.getItem('guest_cart_events') || '[]');
    const guestMachineTests = JSON.parse(localStorage.getItem('guest_mt_cart') || '[]');
    
    const totalCount = guestProducts.length + guestMagazines.length + guestEvents.length + guestMachineTests.length;
    return totalCount;
  } catch (error) {
    console.error('Error calculating guest cart count:', error);
    return 0;
  }
}

/**
 * Update the cart count display in the header
 * Updates both the HTML element and localStorage
 */
export function updateGuestCartDisplay() {
  const totalCount = getGuestCartTotalCount();
  
  // Update the HTML display
  if (typeof $ !== 'undefined') {
    $('.lblCartCount').html(totalCount);
  }
  
  // Update localStorage for consistency
  localStorage.setItem('cartCount', totalCount);
  
  return totalCount;
}

/**
 * Clear all guest cart data
 */
export function clearGuestCart() {
  localStorage.removeItem('guest_cart_products');
  localStorage.removeItem('guest_cart_magazines');
  localStorage.removeItem('guest_cart_events');
  localStorage.removeItem('guest_mt_cart');
  localStorage.removeItem('guest_billing_address');
  localStorage.removeItem('guest_shipping_address');
  localStorage.setItem('cartCount', 0);
  
  if (typeof $ !== 'undefined') {
    $('.lblCartCount').html(0);
  }
}

/**
 * Check if guest cart has products
 * @returns {boolean}
 */
export function guestCartHasProducts() {
  try {
    const guestProducts = JSON.parse(localStorage.getItem('guest_cart_products') || '[]');
    return guestProducts.length > 0;
  } catch (error) {
    console.error('Error checking guest products:', error);
    return false;
  }
}

/**
 * Check if guest cart has magazines
 * @returns {boolean}
 */
export function guestCartHasMagazines() {
  try {
    const guestMagazines = JSON.parse(localStorage.getItem('guest_cart_magazines') || '[]');
    return guestMagazines.length > 0;
  } catch (error) {
    console.error('Error checking guest magazines:', error);
    return false;
  }
}

/**
 * Check if guest cart has events (any type)
 * @returns {boolean}
 */
export function guestCartHasEvents() {
  try {
    const guestEvents = JSON.parse(localStorage.getItem('guest_cart_events') || '[]');
    return guestEvents.length > 0;
  } catch (error) {
    console.error('Error checking guest events:', error);
    return false;
  }
}

/**
 * Check if guest cart has regular events (non-machine test events)
 * @returns {boolean}
 */
export function guestCartHasRegularEvents() {
  try {
    const guestEvents = JSON.parse(localStorage.getItem('guest_cart_events') || '[]');
    const regularEvents = guestEvents.filter(event => 
      event.event_type !== 'Machine Test' && 
      event.event_type !== 'machine test'
    );
    return regularEvents.length > 0;
  } catch (error) {
    console.error('Error checking guest regular events:', error);
    return false;
  }
}

/**
 * Check if guest cart has machine test events
 * @returns {boolean}
 */
export function guestCartHasMachineTests() {
  try {
    const guestMachineTests = JSON.parse(localStorage.getItem('guest_mt_cart') || '[]');
    return guestMachineTests.length > 0;
  } catch (error) {
    console.error('Error checking guest machine tests:', error);
    return false;
  }
}

/**
 * Get cart conflict message for guest users
 * Products, Magazines, Regular Events, and Machine Tests cannot be mixed
 * @param {string} itemTypeToAdd - 'product', 'magazine', 'event', or 'machinetest'
 * @param {function} getLanguage - Language function
 * @returns {string} Conflict message or empty string
 */
export function getGuestCartConflictMessage(itemTypeToAdd, getLanguage) {
  const hasProducts = guestCartHasProducts();
  const hasMagazines = guestCartHasMagazines();
  const hasRegularEvents = guestCartHasRegularEvents();
  const hasMachineTests = guestCartHasMachineTests();
  
  const language = getLanguage ? getLanguage() : 'en';
  
  // Adding product
  if (itemTypeToAdd === 'product') {
    // Products can coexist with machine tests, but not with magazines or regular events
    if (hasMagazines || hasRegularEvents) {
      let items = [];
      if (hasMagazines) items.push(language === 'en' ? 'magazines' : 'मासिके');
      if (hasRegularEvents) items.push(language === 'en' ? 'events' : 'कार्यक्रम');
      
      const itemsText = items.join(language === 'en' ? ' and ' : ' आणि ');
      return language === 'en'
        ? `Cart contains ${itemsText}. Please checkout or clear cart to add products.`
        : `कार्टमध्ये ${itemsText} आहेत. उत्पादने जोडण्यासाठी कृपया चेकआउट करा किंवा कार्ट साफ करा.`;
    }
  }
  
  // Adding magazine
  if (itemTypeToAdd === 'magazine') {
    if (hasProducts || hasRegularEvents) {
      let items = [];
      if (hasProducts) items.push(language === 'en' ? 'products' : 'उत्पादने');
      if (hasRegularEvents) items.push(language === 'en' ? 'events' : 'कार्यक्रम');
      
      const itemsText = items.join(language === 'en' ? ' and ' : ' आणि ');
      return language === 'en'
        ? `Cart contains ${itemsText}. Please checkout or clear cart to add magazines.`
        : `कार्टमध्ये ${itemsText} आहेत. मासिके जोडण्यासाठी कृपया चेकआउट करा किंवा कार्ट साफ करा.`;
    }
  }
  
  // Adding regular event
  if (itemTypeToAdd === 'event') {
    // Prevent adding another regular event if one already exists
    if (hasRegularEvents) {
      return language === 'en'
        ? 'Cart already contains an event. Please checkout or clear cart to add another event.'
        : 'कार्टमध्ये आधीच एक कार्यक्रम आहे. दुसरा कार्यक्रम जोडण्यासाठी कृपया चेकआउट करा किंवा कार्ट साफ करा.';
    }
    if (hasProducts || hasMagazines || hasMachineTests) {
      let items = [];
      if (hasProducts) items.push(language === 'en' ? 'products' : 'उत्पादने');
      if (hasMagazines) items.push(language === 'en' ? 'magazines' : 'मासिके');
      if (hasMachineTests) items.push(language === 'en' ? 'machine tests' : 'मशीन चाचण्या');
      
      const itemsText = items.join(language === 'en' ? ' and ' : ' आणि ');
      return '';
    }
  }
  
  // Adding machine test
  if (itemTypeToAdd === 'machinetest') {
    // Machine tests can coexist with products, but not with magazines or regular events
    if (hasMagazines || hasRegularEvents) {
      let items = [];
      if (hasMagazines) items.push(language === 'en' ? 'magazines' : 'मासिके');
      if (hasRegularEvents) items.push(language === 'en' ? 'events' : 'कार्यक्रम');
      
      const itemsText = items.join(language === 'en' ? ' and ' : ' आणि ');
      return language === 'en'
        ? `Cart contains ${itemsText}. Please checkout or clear cart to add machine tests.`
        : `कार्टमध्ये ${itemsText} आहेत. मशीन चाचण्या जोडण्यासाठी कृपया चेकआउट करा किंवा कार्ट साफ करा.`;
    }
  }
  
  return ''; // No conflict
}

